import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';

import { Redirect } from 'react-router-dom';
import Recaptcha from 'react-grecaptcha';

import { Box, Button, Callout, Flex } from 'core/components';
import { Field, Form, InputGroup } from 'core/form';
import { fields, options } from 'app/forms/config/login';
import { ContentfulDivider } from 'core/components/ContentDivider';
import LinkButton from 'core/components/LinkButton';
import Icon from 'core/components/Icon';
import { IoMdKey } from 'react-icons/io';
import Text from 'core/components/Text';
import PasswordInput from 'core/form/components/PasswordInput';
import { LOGIN } from '@kentik/ui-shared/paths/login';
import { twoFactorImport } from 'app/views/login/2fa';
import { useLoginContext } from 'app/views/login/entry/context';
import VendorLogo from 'app/components/VendorLogo';
import { withOAuthSsoReturn } from 'app/util/oauthSsoReturn';

export const LoginFormInternal = ({ form, authenticating, authenticated, openConfig, showLoginRecaptcha }) => {
  const { handleSubmit } = useLoginContext();
  const [forgotPassword, setForgotPassword] = useState(false);

  const showRecaptcha = openConfig?.recaptchaEnabled && showLoginRecaptcha;
  // if recaptcha is to be shown, update the form rules to make it required.
  form.getField('recaptcha').setRules(showRecaptcha ? 'required' : []);

  const handleSubmitWrapper = useCallback(
    (passedForm, data) =>
      // prefetch lazy-loaded 2-factor page to save time and prevent potential flash of suspense content
      Promise.all([handleSubmit(passedForm, data), twoFactorImport()]).then(() => {
        // reset recaptcha after submit.
        if (showRecaptcha) {
          window.grecaptcha?.reset();
          passedForm.setValue('recaptcha', null);
        }
      }),
    [handleSubmit, showRecaptcha]
  );

  const handleKeyboardSubmit = useCallback(
    (e) => {
      if (e && e.keyCode === 13) {
        if (openConfig && form.dirty && form.valid) {
          form.submit(handleSubmitWrapper);
        }
      }
    },
    [form, handleSubmitWrapper, openConfig]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyboardSubmit);
    return () => document.removeEventListener('keydown', handleKeyboardSubmit);
  }, [handleKeyboardSubmit]);

  const recaptchaCallback = useCallback(
    (response) => {
      form.setValue('recaptcha', response);
    },
    [form]
  );

  const recaptchaExpire = useCallback(() => {
    form.setValue('recaptcha', null);
  }, [form]);

  if (forgotPassword) {
    return <Redirect to={{ pathname: LOGIN.PASSWORD_RECOVERY }} push />;
  }

  const loginButtonProps = { to: LOGIN.SSO_LOOKUP };
  const infobloxButtonProps = { to: withOAuthSsoReturn(LOGIN.INFOBLOX_OIDC) };

  if (form.getValue('user_email')) {
    loginButtonProps.to += `?email=${form.getValue('user_email')}`;
  }

  return (
    <form>
      {form.error && (
        <Callout title="Login Failed" intent="danger" icon="error" my={2}>
          {form.error}
        </Callout>
      )}
      <Field disabled={authenticated || authenticating} name="user_email" showRequired={false} large valid>
        <InputGroup aria-labelledby="email-label" autoFocus autoComplete="username" className="jumbo" />
      </Field>
      <Field disabled={authenticated || authenticating} name="password" showRequired={false} large>
        <PasswordInput aria-labelledby="password-label" showCapsLock showVisibleToggle jumbo />
      </Field>
      {/* negative margin used to offset margins from Field, as this action is closely tied to password field */}
      <Flex flexDirection="row" justifyContent="end" mt="-14px">
        <Button minHeight="16px" intent="primary" minimal onClick={() => setForgotPassword(true)}>
          Forgot your password?
        </Button>
      </Flex>
      {showRecaptcha && (
        <Box my={1}>
          <Recaptcha
            sitekey={openConfig.recaptchaSiteKey}
            callback={recaptchaCallback}
            expiredCallback={recaptchaExpire}
          />
        </Box>
      )}
      <Flex flexDirection="column" justifyContent="space-between" alignItems="center">
        <Button
          disabled={!openConfig || !form.dirty || !form.valid}
          intent="primary"
          large
          fill
          loading={authenticated || authenticating}
          onClick={() => form.submit(handleSubmitWrapper)}
          text="Sign in with Email"
          mb={2}
          mt={1}
        />
        {openConfig && !openConfig.subtenancy && (
          <>
            <ContentfulDivider mb={2}>
              <Text muted>or</Text>
            </ContentfulDivider>

            <Flex flexDirection="column" gap={2} width="100%" alignItems="center">
              <LinkButton large fill {...loginButtonProps}>
                <Icon iconSize={20} icon={IoMdKey} pr={1} /> Sign in with SSO
              </LinkButton>
              {/* /sso/oidc is a backend route; target forces a native navigation instead of SPA routing */}
              <LinkButton large fill {...infobloxButtonProps} target="_self">
                <Icon
                  iconSize={20}
                  pr={2}
                  icon={
                    <VendorLogo
                      name="InfoBlox Inc."
                      logoStyle={{ width: 17, height: 17, verticalAlign: 'middle', marginRight: '7px' }}
                      icon
                    />
                  }
                />
                Sign in with Infoblox
              </LinkButton>
            </Flex>
          </>
        )}
        <Box mt={2}>
          <a href="https://www.kentik.com/go/get-started">Sign up for free!</a>
        </Box>
      </Flex>
    </form>
  );
};

export const LoginForm = Form({ fields, options })(observer(LoginFormInternal));
